<template>
	<div>
		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline"
					>{{ $t("transactions") }}
					<core-pagination-info
						:itemsPerPage="itemsPerPage"
						:numberOfItems="numberOfItems"
						:currentPage="computedPage"
					/>
				</template>
				<template slot="actions">
					<j-button
						v-can="'writeTransactions'"
						type="primary"
						@click="$modal.show('addTransaction')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add_transaction") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Search -->
			<div class="flex wrap xs-12 xs-left mb2">
				<div class="flex xs-12">
					<j-base-input-search
						:placeholder="$t('search_transaction_number')"
						:value="computedSearchTerm"
						@input="onSearch($event)"
					/>
					<j-button
						class="ml2"
						type="tertiary"
						v-if="computedSearchTerm"
						@click="cancelSearch"
					>
						{{ $t("cancel") }}
					</j-button>
				</div>
			</div>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedtransactions"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template v-if="column.field === 'actions'">
								<div class="flex justify-end">
									<crm-icon
										v-can="'writeTransactions'"
										name="edit"
										color="c6"
										class="cu2"
										@click.native="handleUpdate(item)"
									/>
									<crm-icon
										v-can="'writeTransactions'"
										name="delete"
										color="cError"
										class="cu2"
										@click.native="showDeleteDialog(item.id)"
									/>
								</div>
							</template>
							<template v-else>
								<template v-if="column.field === 'transactionNumber'">
									<router-link
										:to="
											`/${
												$route.params.tenantId
											}/transactions/detail/transaction/${item.id}`
										"
									>
										<span v-if="!item[column.field]"> --- </span>
										<span v-else> {{ item[column.field] }} </span>
									</router-link>
								</template>
								<template v-else>
									{{ item[column.field] }}
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<!-- Pagination -->
			<div class="flex xs-12 p4 xs-center mb15">
				<j-pagination
					:amount="numberOfItems"
					:perPage="itemsPerPage"
					:activePage="computedPage"
					@click="onPageChange($event)"
				/>
			</div>

			<core-modal name="addTransaction" :headline="$t('add_transaction')">
				<transaction-create
					@cancel="$modal.hide('addTransaction')"
					@transaction:added="
						$modal.hide('addTransaction'), fetchTransactions()
					"
				/>
			</core-modal>

			<!-- Update Transaction -->
			<core-modal name="updateTransaction" :headline="$t('update_transaction')">
				<transaction-update
					:transactionId="transactionToUpdateId"
					:initialPayload="transactionToUpdatePayload"
					@transaction:updated="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
					@cancel="$modal.hide('updateTransaction')"
				/>
			</core-modal>

			<!-- Delete Confirmation Modal -->
			<j-dialog
				:title="$t('delete_transaction')"
				type="delete"
				@confirm="onDelete(selectedId)"
			>
				{{ $t("errors.are_you_sure_you_want_to_delete_transaction") }}
			</j-dialog>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JPagination from "@/components/JPagination/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JDialog from "@/components/JDialog/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CoreModal from "@/modules/core/components/CoreModal"
import TransactionCreate from "../components/TransactionCreate"
import TransactionUpdate from "../components/TransactionUpdate"
import dayjs from "dayjs"
import api from "../api"
import get from "lodash.get"
import debounce from "lodash.debounce"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JPagination,
		JBaseInputSearch,
		JDialog,
		CoreLayoutContent,
		CorePaginationInfo,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		TransactionCreate,
		TransactionUpdate
	},

	data: () => ({
		isLoading: false,

		// Fetched data
		transactions: [],
		numberOfItems: 0,

		// Pagination
		itemsPerPage: 30,

		// When user clicks on edit icon, we save this id here
		transactionToUpdateId: null,
		transactionToUpdatePayload: null,

		// selected item to delete
		selectedId: null
	}),

	computed: {
		computedPage() {
			return this.$store.state.transactions.listing.page
		},

		computedSearchTerm() {
			return this.$store.state.transactions.listing.searchTerm
		},

		header() {
			return [
				{ text: this.$t("debitNoteNumber"), field: "transactionNumber" },
				{ text: this.$t("when"), field: "dateTime" },
				{ text: this.$t("amount"), field: "formattedAmount" },
				{ text: this.$t("payment_method"), field: "method" },
				{ text: this.$t("status"), field: "localizedStatus" },
				{ text: this.$t("transaction_for"), field: "type" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},

		computedtransactions() {
			const transactions = this.transactions
			if (!transactions) return []

			// Crunch transactions
			// Sort to compare and order dates correctly
			// Also reverse them so that most recent one is the most top
			return transactions.map(item => ({
				transactionNumber: item.transactionNumber,
				id: item.id,
				dateTime: item.dateTime
					? dayjs(item.dateTime).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,
				formattedAmount: item.formattedTransaction,
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				localizedStatus: item.status, // TODO: localized
				method: item.method,
				localizedMethod: item.method
					? this.$t(`payment_methods_enums.${item.method}`)
					: null,
				type: item.object
					? this.$t(`transaction_type_enums.${item.object.__typename}`)
					: null,
				object: item.object ? JSON.stringify(item.object) : null
			}))
		}
	},

	methods: {
		async fetchTransactions() {
			// Pagination
			const skip = (this.computedPage - 1) * this.itemsPerPage
			const limit = this.itemsPerPage
			const sort = {
				fieldName: "createdAt",
				type: "desc"
			}

			// Search
			let where = null
			if (this.computedSearchTerm) {
				where = { transactionNumber: this.computedSearchTerm }
			}

			this.isLoading = true
			const res = await api.queryTransactions({ skip, limit, where, sort })
			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.transactions = get(res, "data.transactions.list", [])
			this.numberOfItems = get(res, "data.transactions.totalCount", 0)

			// Return page - 1 if remove the last item of page and list page > 1
			if (!this.transactions.length && this.computedPage > 1) {
				const currentPage = this.computedPage - 1

				this.$store.commit("transactions/SET_PAGE", currentPage)

				const skipSecond = (currentPage - 1) * this.itemsPerPage
				const response = await api.queryTransactions({
					skipSecond,
					limit,
					where
				})

				// Success
				this.transactions = get(response, "data.transactions.list", [])
				this.numberOfItems = get(response, "data.transactions.totalCount", 0)
			}
		},

		onPageChange(pageIndex) {
			this.$store.commit("transactions/SET_PAGE", pageIndex)
			this.fetchTransactions()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			const payload = value ? value.trim() : null
			this.$store.commit("transactions/SET_SEARCH_TERM", payload)
			this.$store.commit("transactions/SET_PAGE", 1)

			this.fetchTransactions()
		}, 350),

		cancelSearch() {
			this.$store.commit("transactions/SET_SEARCH_TERM", null)
			this.$store.commit("transactions/SET_PAGE", 1)
			this.onSearch()
		},

		handleUpdate(item) {
			this.transactionToUpdateId = item.id
			this.transactionToUpdatePayload = {
				transactionNumber: item.transactionNumber,
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				dateTime: item.dateTime,
				method: item.method,
				object: item.object
			}

			// Open popup
			this.$modal.show("updateTransaction")
		},

		showDeleteDialog(id) {
			this.$modal.show("j-dialog")
			this.selectedId = id
		},

		async onDelete() {
			this.isLoading = true
			const res = await api.deleteTransaction({
				transactionId: this.selectedId
			})
			this.isLoading = false

			if (res?.data?.deleteTransaction?.status === "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("transaction_removed")
					}
				})

				this.fetchTransactions()
			} else {
				this.$store.dispatch("showError", [this.$t("something_went_wrong")])
			}
		}
	},

	created() {
		this.fetchTransactions()
	}
}
</script>
<style scoped>
.justify-end {
	justify-content: flex-end;
}
</style>
